import { Component, type ErrorInfo, type ReactNode } from "react";
import { BootScreen } from "../boot/BootScreen";
import { captureException } from "../../lib/monitoring";
import { isChunkLoadError, isStaleReloadPending, reloadForStaleChunk } from "../../lib/chunkReload";

/**
 * ⚠️ **A CORUJA SAIU DAQUI (13/08/2026, pedido do fundador).**
 *
 * As duas telas desenhavam `LuraOwl` — um asset descontinuado — e a de erro
 * ainda dizia "Ops, a coruja derrubou uns livros". Era o último lugar do
 * produto onde o mascote aparecia: o passo 7 do onboarding e o modal de
 * conclusão já o tinham perdido, pela mesma regra (o herói é a coisa
 * conquistada, ou a marca — nunca um personagem que o app não usa mais).
 *
 * As duas telas passaram a ser a `BootScreen`, que é a mesma superfície de
 * espera/falha do resto do boot: mesma marca, mesmo alvo de 44px, mesmo
 * contraste medido nos 11 temas, e o painel de log no toque longo de 5s vem
 * junto — que é exatamente o que falta quando um erro de React derruba o app
 * num aparelho ao qual não temos acesso.
 *
 * Dois defeitos herdados morreram no caminho:
 *  · os fallbacks inline eram de tema ESCURO (`#0a0a0a` / `#f4f4f4`) num app
 *    de papel claro — se a variável não resolvesse, a tela de erro saía preta;
 *  · a copy usava `text-primary`/`text-text-muted`, gramática pré-redesign.
 */

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  staleChunk: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, staleChunk: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // BUG-322: com um reload pendente (esperando o service worker novo), o
    // `lazy()` do chunk velho estoura com um TypeError qualquer — é o mesmo caso.
    const staleChunk = isChunkLoadError(error) || isStaleReloadPending();
    return { hasError: true, error, staleChunk };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // BUG-37: chunk obsoleto pós-deploy não é bug — é cliente com index.html
    // velho em cache. Recarrega uma vez em silêncio; só cai na tela de erro se
    // o reload não resolver (o guard limita as tentativas).
    const stale = isChunkLoadError(error) || isStaleReloadPending();
    if (stale && reloadForStaleChunk()) return;
    // BUG-322: tentativas esgotadas — sem isto a tela ficava presa em
    // "Atualizando o app…", sem botão.
    if (this.state.staleChunk) this.setState({ staleChunk: false });

    console.error("[Lura] Uncaught error:", error, info.componentStack);
    captureException(error, {
      kind: isChunkLoadError(error) ? "stale-chunk" : "react-boundary",
      componentStack: info.componentStack ?? undefined,
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    // Enquanto o reload não assume, evita expor a tela de erro por um caso que
    // se resolve sozinho em milissegundos.
    if (this.state.staleChunk) {
      return <BootScreen message="Atualizando o app…" />;
    }

    return (
      <BootScreen
        failureCode={import.meta.env.DEV ? (this.state.error?.message ?? "ERRO") : "APP_ERROR"}
        onRetry={() => window.location.reload()}
      />
    );
  }
}
